import { z } from 'zod'

// Common keyboard sequences to reject
const KEYBOARD_SEQUENCES = [
  'qwerty', 'qwertyui', 'qwertyuiop',
  'asdfgh', 'asdfghjk', 'asdfghjkl',
  'zxcvbn', 'zxcvbnm',
  '12345', '123456', '1234567', '12345678', '123456789',
  '23456789', '34567890',
  'abcdef', 'abcdefg', 'abcdefgh',
  'qazwsx', 'qazwsxedc',
]

// Common/weak passwords to reject
const COMMON_PASSWORDS = new Set([
  'password', '123456', '12345678', 'qwerty', 'abc123',
  'monkey', '1234567', 'letmein', 'trustno1', 'dragon',
  'baseball', '111111', 'iloveyou', 'master', 'sunshine',
  'ashley', 'bailey', 'passw0rd', 'shadow', '123123',
  '654321', 'superman', 'qazwsx', 'michael', 'football',
  'password1', 'password123', 'admin', 'welcome', 'login',
  'admin123', 'root', 'toor', 'pass', 'pass123',
  'test', 'test123', 'guest', 'guest123', 'user', 'user123',
  'hello', 'hello123', 'changeme', 'qwerty123', 'qwerty1',
  'abc1234', 'abcdef', 'abcdef1', 'password!', 'password1!',
])

/**
 * Shared password policy:
 * - 12+ characters → passes automatically
 * - 8–11 characters → must have uppercase, lowercase, digit, and special character
 * - No 4+ identical consecutive characters (aaaa)
 * - No common keyboard sequences (qwerty, 12345, etc.)
 * - Not in common password list
 * - Max 128 characters (DoS prevention)
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long (max 128 characters)')
  .refine(
    (pwd) => {
      if (pwd.length >= 12) return true
      const hasUpper = /[A-Z]/.test(pwd)
      const hasLower = /[a-z]/.test(pwd)
      const hasDigit = /\d/.test(pwd)
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/`~;]/.test(pwd)
      return hasUpper && hasLower && hasDigit && hasSpecial
    },
    'Password must be 12+ characters, or 8+ with uppercase, lowercase, number, and special character'
  )
  .refine(
    (pwd) => !(/(.)\1{3,}/.test(pwd)),
    'Password cannot contain 4 or more identical characters in a row'
  )
  .refine(
    (pwd) => {
      const lower = pwd.toLowerCase()
      return !KEYBOARD_SEQUENCES.some((seq) => lower.includes(seq))
    },
    'Password cannot contain common keyboard sequences'
  )
  .refine(
    (pwd) => !COMMON_PASSWORDS.has(pwd.toLowerCase()),
    'This password is too common. Please choose a more unique password.'
  )

// Schema for the reset-password / set-password page
export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

// Schema for the change-password form on the account page
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>
